// src/components/Admin/PostManager/PostsTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, TextField, TableContainer, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, Pagination, Stack, Button, CircularProgress,
  Alert, MenuItem, Select, Typography, IconButton, Tooltip, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useUser } from '../../../contexts/UserContext';
import api from '../../../api/axios';
import ReasonModal from './ReasonModal';
import * as XLSX from 'xlsx';   // ✅ 추가

const ROWS_PER_PAGE = 15;

/* ────────────────────────────────────────────────────────── */
export default function PostsTable() {
  const { userInfo } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // md 미만은 아이콘 UI

  const [allPosts, setAllPosts] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  /* ───────── 데이터 로드 ───────── */
  const fetchAllPosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/posts/all/');
      setAllPosts(res.data.results || []);
    } catch {
      setError('게시글 목록을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchAllPosts(); }, [fetchAllPosts]);

  /* ───────── 필터링 ───────── */
  const filteredPosts = useMemo(() => (
    allPosts
      .filter(p => {
        if (statusFilter === 'visible') return !p.is_deleted && !p.is_hidden;
        if (statusFilter === 'hidden') return p.is_hidden;
        if (statusFilter === 'deleted') return p.is_deleted;
        return true;
      })
      .filter(p =>
        !search.trim() ||
        (p.title || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.nickname || '').toLowerCase().includes(search.toLowerCase())
      )
  ), [allPosts, search, statusFilter]);

  const totalPages = Math.ceil(filteredPosts.length / ROWS_PER_PAGE) || 1;
  const paginated = filteredPosts.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);
  useEffect(() => setPage(1), [search, statusFilter]);

  // ───────── 엑셀 내보내기 ─────────
  const handleExportExcel = () => {
    const rows = filteredPosts.map((p) => ({
      ID: p.id,
      카테고리: p.category_name || '',           // PostListSerializer에서 내려주고 있을 가능성 높음
      제목: p.title || '',
      작성자: p.nickname || '',
      익명여부: p.is_anonymous ? 'Y' : 'N',
      상태: p.is_deleted ? '삭제' : (p.is_hidden ? '블라인드' : '공개'),
      생성일: p.created_at
        ? new Date(p.created_at).toLocaleString('ko-KR')
        : '',
      수정일: p.updated_at
        ? new Date(p.updated_at).toLocaleString('ko-KR')
        : '',
      좋아요수: p.like_count ?? '',
      조회수: p.view_count ?? '',
    }));

    if (!rows.length) {
      alert('엑셀로 내보낼 게시글이 없습니다.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Posts');

    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    XLSX.writeFile(workbook, `posts_${today}.xlsx`);
  };

  /* ───────── 모달 핸들러 ───────── */
  const openModal = (id, type) => {
    setPendingAction({ id, type });
    setModalOpen(true);
  };
  const handleAction = async memo => {
    const { id, type } = pendingAction;
    setModalOpen(false);
    try {
      if (type === 'soft_delete')
        await api.post(`/api/posts/${id}/soft-delete/`, { memo });
      else if (type === 'hard_delete')
        await api.delete(`/api/posts/${id}/permanent-delete/`, { data: { memo } });
      else if (type === 'blind' || type === 'unblind')
        await api.post(`/api/posts/${id}/blind/`, { is_hidden: type === 'blind', memo });
      alert('처리되었습니다.');
      await fetchAllPosts();
    } catch {
      alert('처리에 실패했습니다.');
    }
  };

  /* ───────── 로딩 / 에러 ───────── */
  if (loading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  /* ───────── 공통 IconButton 스타일 ───────── */
  const iconSx = { p: 0.25, width: 24, height: 24, lineHeight: 1 };

  /* ───────── 렌더 ───────── */
  return (
    <Box sx={{ '@media (max-width:600px)': { transform: 'scale(0.9)', transformOrigin: 'top left' } }}>
      {/* 검색·필터 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="제목/닉네임"
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <Select
          size="small"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">전체</MenuItem>
          <MenuItem value="visible">공개</MenuItem>
          <MenuItem value="hidden">블라인드</MenuItem>
          <MenuItem value="deleted">삭제</MenuItem>
        </Select>
        <Typography variant="body2" color="text.secondary">
          총&nbsp;{allPosts.length}개 / 현재&nbsp;{filteredPosts.length}개
        </Typography>
        {/* ✅ 오른쪽 정렬용 spacer + 엑셀 버튼 */}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          size="small"
          onClick={handleExportExcel}
        >
          📥 엑셀로 내보내기
        </Button>
      </Box>

      {/* 테이블 */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 2,
          '& td, & th': {
            borderBottom: '1px solid #eee',
            p: 1.25,
            whiteSpace: 'nowrap',
            '@media (max-width:600px)': { p: 0.75, fontSize: '0.8rem' },
          },
          '& th': { bgcolor: 'grey.50', fontWeight: 600 },
          '@media (max-width:600px)': { width: 'max-content', overflowX: 'auto' },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center">ID</TableCell>
              <TableCell align="left">제목</TableCell>
              <TableCell align="center">작성자</TableCell>
              <TableCell align="center">작성일</TableCell>
              <TableCell align="center">상태</TableCell>
              <TableCell align="center" sx={{ width: { xs: 84, md: 120 } }}>관리</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  🔍 조건에 맞는 게시글이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              paginated.map(post => {
                const titleCell = post.is_deleted
                  ? '삭제된 게시글입니다'
                  : post.is_hidden
                    ? '블라인드된 게시글입니다'
                    : (
                      <a
                        href={`/main/posts/${post.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: '#1976d2' }}
                      >
                        {post.title || '-'}
                      </a>
                    );

                const stateLabel = post.is_deleted
                  ? '삭제'
                  : post.is_hidden ? '블라인드' : '공개';

                /* ─── 관리 영역 ─── */
                const actions = !isMobile ? (
                  <Stack direction="row" spacing={1} justifyContent="center">
                    {!post.is_deleted && (
                      <>
                        <Button
                          size="small"
                          color={post.is_hidden ? 'primary' : 'secondary'}
                          onClick={() => openModal(post.id, post.is_hidden ? 'unblind' : 'blind')}
                        >
                          {post.is_hidden ? '해제' : '블라인드'}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => openModal(post.id, 'soft_delete')}
                        >
                          삭제
                        </Button>
                      </>
                    )}
                    {userInfo?.role === 'master' && (
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => openModal(post.id, 'hard_delete')}
                      >
                        영구삭제
                      </Button>
                    )}
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={0.25} justifyContent="center" alignItems="center">
                    {!post.is_deleted && (
                      <>
                        <Tooltip title={post.is_hidden ? '숨김 해제' : '블라인드'}>
                          <IconButton
                            size="small"
                            sx={iconSx}
                            color={post.is_hidden ? 'primary' : 'secondary'}
                            onClick={() => openModal(post.id, post.is_hidden ? 'unblind' : 'blind')}
                          >
                            {post.is_hidden
                              ? <VisibilityIcon sx={{ fontSize: 18 }} />
                              : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                          <IconButton
                            size="small"
                            sx={iconSx}
                            color="error"
                            onClick={() => openModal(post.id, 'soft_delete')}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {userInfo?.role === 'master' && (
                      <Tooltip title="영구삭제">
                        <IconButton
                          size="small"
                          sx={iconSx}
                          color="warning"
                          onClick={() => openModal(post.id, 'hard_delete')}
                        >
                          <DeleteForeverIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                );

                return (
                  <TableRow key={post.id} hover sx={{ '&:hover td': { bgcolor: 'grey.100' }, '& td:nth-of-type(2)': { fontWeight: 500 } }}>
                    <TableCell align="center">{post.id}</TableCell>
                    <TableCell align="left">{titleCell}</TableCell>
                    <TableCell align="center">{post.is_anonymous ? `익명(${post.nickname})` : post.nickname || '-'}</TableCell>
                    <TableCell align="center">{new Date(post.created_at).toLocaleDateString('ko-KR')}</TableCell>
                    <TableCell align="center">{stateLabel}</TableCell>
                    <TableCell align="center">{actions}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 4, '@media (max-width:600px)': { transform: 'scale(0.9)' } }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            showFirstButton
            showLastButton
            shape="rounded"
            color="primary"
            size={isMobile ? 'small' : 'medium'}
          />
        </Box>
      )}

      {/* 사유 입력 모달 */}
      <ReasonModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleAction}
        actionLabel={
          pendingAction?.type === 'soft_delete' ? '삭제'
            : pendingAction?.type === 'hard_delete' ? '영구삭제'
              : pendingAction?.type === 'blind' ? '숨김' : '해제'
        }
      />
    </Box>
  );
}
